import { parseXlsxBuffer, type ParsedSource } from './importClients';

/* ============================================================
   Google Drive Picker — "Browse Drive" in the import wizard.

   Scope strategy: drive.file (NON-sensitive — no Google verification
   audit). The app can only read files the coach explicitly picks in
   Google's own Picker UI; nothing else in their Drive is reachable.
   Multi-select is on, so several sheets import in one pass.

   Both Google scripts (GIS auth + Picker) lazy-load on first use —
   nothing is fetched for coaches who never touch Drive.
============================================================ */

const CLIENT_ID = '128943924385-5a5fpfro22g5lso1qh6mpo2nm62ng1po.apps.googleusercontent.com';
// Project number — ties picked files to this app for the drive.file grant.
const APP_ID = '128943924385';
// Public-by-design browser key, restricted to the Picker API + our referrers.
const API_KEY = import.meta.env.VITE_GOOGLE_PICKER_API_KEY as string;

const SCOPE = 'https://www.googleapis.com/auth/drive.file';

declare global {
  interface Window {
    google?: any;
    gapi?: any;
  }
}

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Couldn't load ${src}`));
    document.head.appendChild(s);
  });

async function ensureGoogleLibs(): Promise<void> {
  await Promise.all([
    loadScript('https://accounts.google.com/gsi/client'),
    loadScript('https://apis.google.com/js/api.js'),
  ]);
  await new Promise<void>((resolve) => window.gapi.load('picker', () => resolve()));
}

// One consent popup per session; the token is kept in memory only.
let cachedToken: { token: string; expiresAt: number } | null = null;

async function getAccessToken(): Promise<string> {
  if (cachedToken && Date.now() < cachedToken.expiresAt - 60_000) return cachedToken.token;
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPE,
      callback: (resp: any) => {
        if (resp.error) return reject(new Error(resp.error_description || resp.error));
        cachedToken = { token: resp.access_token, expiresAt: Date.now() + (resp.expires_in || 3600) * 1000 };
        resolve(resp.access_token);
      },
      error_callback: (err: any) => reject(new Error(err?.message || 'Google sign-in was closed.')),
    });
    client.requestAccessToken();
  });
}

interface PickedDoc { id: string; name: string; }

function openPicker(token: string): Promise<PickedDoc[]> {
  return new Promise((resolve) => {
    const g = window.google.picker;
    const view = new g.DocsView(g.ViewId.SPREADSHEETS).setIncludeFolders(true);
    const picker = new g.PickerBuilder()
      .addView(view)
      .enableFeature(g.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setAppId(APP_ID)
      .setOrigin(window.location.origin)
      .setTitle('Pick client sheets')
      .setCallback((data: any) => {
        if (data.action === g.Action.PICKED) {
          resolve((data.docs || []).map((d: any) => ({ id: d.id, name: d.name })));
        } else if (data.action === g.Action.CANCEL) {
          resolve([]);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

// Export as a full xlsx workbook (CSV would silently drop every tab but the
// first) and parse ALL tabs with the shared SheetJS path.
async function exportSheetWorkbook(fileId: string, token: string): Promise<ArrayBuffer> {
  const mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${encodeURIComponent(mime)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`export failed (${res.status})`);
  return res.arrayBuffer();
}

/* The whole flow: consent (once) → Google's picker (multi-select) →
   export each picked sheet as a workbook → every tab becomes a ParsedSource.
   Returns [] when the coach closes the picker without choosing. */
export async function pickSheetsFromDrive(): Promise<ParsedSource[]> {
  await ensureGoogleLibs();
  const token = await getAccessToken();
  const docs = await openPicker(token);
  if (docs.length === 0) return [];

  const sources: ParsedSource[] = [];
  const failures: string[] = [];
  await Promise.all(
    docs.map(async (doc) => {
      try {
        const tabs = await parseXlsxBuffer(await exportSheetWorkbook(doc.id, token), doc.name);
        if (tabs.length > 0) sources.push(...tabs);
        else failures.push(doc.name);
      } catch {
        failures.push(doc.name);
      }
    })
  );
  if (sources.length === 0 && failures.length > 0) {
    throw new Error(`Couldn't read ${failures.join(', ')} — are they Google Sheets?`);
  }
  return sources;
}
